<template>
  <div />
</template>

<script lang="ts">
import { defineComponent, onMounted, onUnmounted } from "vue";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import { alertController, toastController } from "@ionic/vue";
import { useUserStore } from "@/common/store/user.store";
import {
  getShowPushEnablePromptEventName,
  markPushPromptDismissedState,
  requestNotificationPermission,
} from "@/common/services/notifications.service";

interface PushPromptRequestDetail {
  userId?: string;
  resolve?: (enabled: boolean) => void;
}

type AlertElement = Awaited<ReturnType<typeof alertController.create>>;

export default defineComponent({
  name: "VPushNotificationPrompt",

  setup() {
    const { t } = useI18n();
    const userStore = useUserStore();
    const { currentUser } = storeToRefs(userStore);
    const showPushEnablePromptEventName = getShowPushEnablePromptEventName();
    let isPromptOpen = false;

    async function showToast(
      message: string,
      color: "success" | "warning" | "danger" = "success",
    ): Promise<void> {
      const toast = await toastController.create({
        message,
        duration: 3000,
        color,
        position: "bottom",
      });
      await toast.present();
    }

    async function enablePushFromPrompt(
      alert: AlertElement,
      userId: string,
      resolve?: (enabled: boolean) => void,
    ): Promise<void> {
      try {
        const permission = await requestNotificationPermission();
        if (permission !== "granted") {
          await alert.dismiss(null, "cancel");

          if (permission === "denied") {
            await showToast(t("message.accountHome.accountInfo.pushBlockedDescription"), "warning");
          } else {
            await showToast(t("message.accountHome.accountInfo.pushEnableError"), "danger");
          }

          resolve?.(false);
          return;
        }

        await alert.dismiss(null, "confirm");
        const enabled = await userStore.enablePushNotifications();

        if (enabled) {
          await showToast(t("message.accountHome.accountInfo.pushEnabledToast"));
          resolve?.(true);
          return;
        }

        if (typeof window !== "undefined" && "Notification" in window) {
          if (Notification.permission === "denied") {
            await showToast(t("message.accountHome.accountInfo.pushBlockedDescription"), "warning");
          } else {
            await showToast(t("message.accountHome.accountInfo.pushEnableError"), "danger");
          }
        }
        resolve?.(false);
      } catch (error) {
        console.error("Error enabling push notifications from prompt:", error);
        await alert.dismiss(null, "cancel");
        await showToast(t("message.accountHome.accountInfo.pushSettingsUpdateError"), "danger");
        resolve?.(false);
      }
    }

    async function showPrompt(userId: string, resolve?: (enabled: boolean) => void): Promise<void> {
      if (isPromptOpen) {
        resolve?.(false);
        return;
      }

      isPromptOpen = true;
      const alert = await alertController.create({
        header: t("message.pushPrompt.title"),
        message: t("message.pushPrompt.message"),
        backdropDismiss: false,
        buttons: [
          {
            text: t("message.pushPrompt.dismiss"),
            role: "cancel",
            handler: () => {
              markPushPromptDismissedState(userId);
              resolve?.(false);
            },
          },
          {
            text: t("message.pushPrompt.activate"),
            handler: () => {
              void enablePushFromPrompt(alert, userId, resolve);
              return false;
            },
          },
        ],
      });

      await alert.present();
      await alert.onDidDismiss();
      isPromptOpen = false;
    }

    const onShowPushPrompt = (event: Event) => {
      const detail = (event as CustomEvent<PushPromptRequestDetail>).detail;
      const userId = detail?.userId || currentUser.value?.id;
      if (!userId) {
        detail?.resolve?.(false);
        return;
      }
      void showPrompt(userId, detail?.resolve);
    };

    onMounted(() => {
      window.addEventListener(showPushEnablePromptEventName, onShowPushPrompt);
    });

    onUnmounted(() => {
      window.removeEventListener(showPushEnablePromptEventName, onShowPushPrompt);
    });
  },
});
</script>
